import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import CountUp from "react-countup";
import { ArrowUpRight, Sparkle, Confetti, Monitor, DeviceMobile } from "@phosphor-icons/react";
import ClientMarquee from "@/components/ClientMarquee";
import { useSettings } from "@/contexts/SettingsContext";
import { resolveImage } from "@/lib/api";
import { usePageTitle } from "@/hooks/usePageTitle";
import Seo from "@/components/Seo";
import MoveAnnouncement from "@/components/MoveAnnouncement";

const ACCENT_MAP = { peach: "bg-ot-peach", mint: "bg-ot-mint", coral: "bg-ot-coral", indigo: "bg-ot-indigo text-white" };
const ACCENT_ROTATE = ["bg-ot-peach", "bg-ot-mint", "bg-ot-coral"];
const SERVICE_ICONS = [Confetti, Monitor, DeviceMobile];

export default function Home() {
  const { settings } = useSettings();
  const [idx, setIdx] = useState(0);
  usePageTitle("home");

  const words = settings?.hero_rotating_words || ["Events", "Brands", "Registrations"];

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % words.length), 2200);
    return () => clearInterval(t);
  }, [words.length]);

  if (!settings) return null;

  const stats = [
    { label: "Employees", value: settings.stats?.employees ?? 15, suffix: "" },
    { label: "Events / year", value: settings.stats?.events_per_year ?? 525, suffix: "" },
    { label: "Countries", value: settings.stats?.countries ?? 42, suffix: "" },
    { label: "Clients", value: settings.stats?.clients ?? 250, suffix: "+" },
    { label: "Visitors / year", value: settings.stats?.visitors_per_year ?? 100000, suffix: "+" },
  ];

  return (
    <div data-testid="home-page">
      <MoveAnnouncement />
      <Seo
        path="/"
        noTemplate
        title="OneTec — Event Registration, Badging & Brand Activation in Belgium since 2002"
        description="Belgian event technology company since 2002. Event registration, onsite badging, brand activation, photobooth rental, event accreditation systems and IT/AV rental — across Belgium and 42 countries."
        image={settings.hero_image_url}
        schema={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "What does OneTec do?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "OneTec is a Belgium-based event technology company offering three services: Brand Activation (photo/video experiences, AI face swap, VR, light painting, mosaic photo, transfer press), Event Technology (online registration, on-site badging, access control, mobile voting, lead trackers, webinars), and IT & AV Rental (laptops, screens, LED walls, projectors, full AV setups). Founded in 2002.",
              },
            },
            {
              "@type": "Question",
              name: "Where is OneTec located?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "OneTec SRL is headquartered at Tollaan 75, B-1932 Zaventem, Belgium. Phone: +32 2 702 36 36. Email: hello@onetec.eu. VAT: BE0478.889.493.",
              },
            },
            {
              "@type": "Question",
              name: "Which countries does OneTec serve?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "OneTec operates in 42 countries, with primary coverage of Belgium, France, Netherlands, Luxembourg, Germany, United Kingdom and the rest of Europe. The company delivers 525 events per year for 250+ clients.",
              },
            },
            {
              "@type": "Question",
              name: "Who are OneTec's clients?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "OneTec has worked with major international brands including Amazon, Nike, Samsung, Coca-Cola, PepsiCo, Philips, Nissan, RTL, Triumph, Solvay, Infrabel and Club Brugge — over 250 brands in total.",
              },
            },
            {
              "@type": "Question",
              name: "When was OneTec founded?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "OneTec was founded in 2002 in Belgium and has been delivering event technology, brand activations and IT/AV rental services for over 22 years.",
              },
            },
            {
              "@type": "Question",
              name: "What technology does OneTec offer for event registration?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "OneTec provides branded online registration pages with payment integration, on-site self-check-in kiosks with on-demand badge printing (PVC or paper, QR/RFID), award-winning badging technology that processes 1000+ attendees per hour, multi-zone access control, and lead-tracking apps for exhibitors.",
              },
            },
          ],
        }}
      />
      {/* HERO */}
      <section className="relative overflow-hidden border-b-2 border-ot-fg" data-testid="hero" aria-labelledby="hero-h1">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-12 md:pt-24 pb-16 md:pb-32 grid md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-8">
            <div className="inline-flex items-center gap-2 border-2 border-ot-fg px-3 py-1 mb-6 bg-white">
              <Sparkle weight="fill" className="text-ot-coral" size={16} />
              <span className="text-xs uppercase tracking-widest font-bold">{settings.hero_badge}</span>
            </div>
            <h1 id="hero-h1" className="font-display font-bold uppercase tracking-tighter leading-[0.88] text-[12vw] md:text-[7.5vw] lg:text-[6vw] xl:text-[5.5vw]">
              {(settings.hero_headline_prefix || "We Boost Your").split(" ").slice(0, -1).join(" ") && (
                <span className="block">{(settings.hero_headline_prefix || "We Boost Your").split(" ").slice(0, -1).join(" ")}</span>
              )}
              <span className="block">{(settings.hero_headline_prefix || "We Boost Your").split(" ").slice(-1)}</span>
              <span className="block relative h-[1em] overflow-visible">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={words[idx]}
                    initial={{ y: "100%", opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: "-100%", opacity: 0 }}
                    transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                    className={`inline-block px-2 md:px-4 border-2 border-ot-fg whitespace-nowrap ${ACCENT_ROTATE[idx % ACCENT_ROTATE.length]}`}
                    data-testid="hero-rotating-word"
                  >
                    {words[idx]}.
                  </motion.span>
                </AnimatePresence>
              </span>
            </h1>
            {settings.hero_tagline && (
              <p className="mt-6 font-display text-xl md:text-2xl uppercase tracking-tight text-ot-indigo">
                {settings.hero_tagline}
              </p>
            )}
            <p className="mt-5 text-lg md:text-xl max-w-xl text-ot-fg/80">{settings.hero_description}</p>
            {/* SEO-rich keyword line — visible to users and crawlers */}
            <p className="mt-3 text-sm text-ot-fg/60 max-w-xl">
              Event registration · Onsite badging · Brand activation · Photobooth rental · Event accreditation systems — <strong className="font-semibold text-ot-fg">in Belgium and across Europe</strong>.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/contact" className="nb-btn" data-testid="hero-cta-primary">
                Start a project <ArrowUpRight weight="bold" />
              </Link>
              <Link to="/products" className="nb-btn nb-btn-ghost" data-testid="hero-cta-secondary">
                See the products <ArrowUpRight weight="bold" />
              </Link>
            </div>
          </div>

          <div className="md:col-span-4 relative hidden md:block">
            <div className="absolute -top-4 -right-4 w-full h-full bg-ot-peach border-2 border-ot-fg -z-0" aria-hidden />
            <img
              src={resolveImage(settings.hero_image_url)}
              alt="Event"
              fetchpriority="high"
              decoding="async"
              className="relative z-10 border-2 border-ot-fg w-full h-[520px] object-cover grayscale contrast-125"
            />
            <div className="relative z-10 mt-4 bg-ot-indigo text-white p-4 border-2 border-ot-fg">
              <p className="font-display uppercase text-sm">{settings.hero_image_caption}</p>
              <p className="text-xs text-white/70 mt-1">{settings.hero_image_subcaption}</p>
            </div>
          </div>
        </div>
      </section>

      <ClientMarquee />

      {/* SERVICES */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28" data-testid="home-services">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <p className="text-xs uppercase tracking-widest font-bold text-ot-indigo">What we do</p>
            <h2 className="font-display text-5xl md:text-7xl uppercase tracking-tighter leading-none mt-3">
              Three pillars.<br />Zero filler.
            </h2>
          </div>
          <Link to="/services" className="nb-btn nb-btn-ghost shrink-0" data-testid="services-link">
            All services <ArrowUpRight weight="bold" />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {(settings.services || []).map((s, i) => {
            const Icon = SERVICE_ICONS[i] || Confetti;
            return (              <motion.article
                key={s.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="nb-card group"
                data-testid={`service-card-${i}`}
              >
                <div className={`${ACCENT_MAP[s.accent] || "bg-ot-peach"} border-b-2 border-ot-fg aspect-[4/3] relative overflow-hidden`}>
                  {s.image_url && (
                    <img
                      src={resolveImage(s.image_url)}
                      alt={s.title}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover mix-blend-multiply group-hover:scale-105 transition-transform duration-500"
                    />
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <Icon size={28} weight="bold" aria-hidden="true" />
                    <h3 className="font-display text-2xl uppercase">{s.title}</h3>
                  </div>
                  <p className="text-ot-fg/80">{s.description}</p>
                </div>
              </motion.article>
            );
          })}
        </div>
      </section>

      {/* STATS */}
      <section className="border-y-2 border-ot-fg bg-ot-indigo text-white" data-testid="stats-section">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <p className="text-xs uppercase tracking-widest font-bold text-ot-peach">By the numbers</p>
          <h2 className="font-display text-5xl md:text-7xl uppercase tracking-tighter mt-3 leading-none">
            Loud results.<br />Quiet confidence.
          </h2>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6">
            {stats.map((s, i) => (
              <div key={s.label} className="border-2 border-white/30 p-5 hover:bg-ot-peach hover:text-ot-fg hover:border-ot-peach transition-colors" data-testid={`stat-${i}`}>
                <div className="font-display text-5xl md:text-6xl tracking-tighter">
                  <CountUp end={s.value} duration={2.5} separator="," suffix={s.suffix} enableScrollSpy scrollSpyOnce />
                </div>
                <div className="mt-2 text-sm uppercase tracking-wide">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {(settings.values || []).map((v, i) => (
            <div key={v.title} className="nb-card p-8" data-testid={`value-${i}`}>
              <span className={`font-display text-7xl leading-none ${i === 1 ? "text-ot-coral" : "text-ot-indigo"}`}>0{i + 1}</span>
              <h3 className="font-display text-3xl uppercase mt-4">{v.title}</h3>
              <p className="mt-3 text-ot-fg/80">{v.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-16">
        <div className="bg-ot-peach border-2 border-ot-fg p-10 md:p-20 text-center shadow-[12px_12px_0px_#111111]">
          <h2 className="font-display text-5xl md:text-8xl uppercase tracking-tighter leading-[0.9]">
            Don't be shy.<br />Say hello.
          </h2>
          <p className="mt-6 max-w-lg mx-auto text-lg">
            Brief us on your next event and we'll reply within 24 hours with a first read.
          </p>
          <Link to="/contact" className="nb-btn mt-8" data-testid="home-bottom-cta">
            Start a project <ArrowUpRight weight="bold" />
          </Link>
        </div>
      </section>
    </div>
  );
}
